import PropTypes from "prop-types";
import React from "react";
import {
  Box,
  Typography,
  Divider,
  Button,
  Paper,
  AppBar,
  Container
} from "@mui/material";
import { TextField } from "formik-mui";
import { Field, Formik, Form } from "formik";
import makeStyles from "@mui/styles/makeStyles";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import CopyrightMessage from "../../components/CopyrightMessage/CopyrightMessage";

const useStyles = makeStyles(theme => ({
  root: {
    background: `radial-gradient(79.83% 135.09% at 79.83% 119.68%, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
    width: "100vw",
    height: "100vh",
    display: "flex",
    flexDirection: "column"
  },
  formContainer: {
    width: "100%",
    marginTop: theme.spacing(6),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    maxWidth: 524,
    "& > div": {
      marginBottom: theme.spacing(4.5)
    }
  },
  divider: {
    width: "100%",
    margin: theme.spacing(6, 0)
  },
  footer: {
    width: "100%",
    height: 48,
    background: "#F5F5F5",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  headerBar: {
    height: 72,
    backgroundColor: theme.palette.common.white
  }
}));

const geUpdatePasswordSchema = t => {
  return Yup.object().shape({
    password: Yup.string().required(t("validationMessages.passwordRequired")),
    confirmPassword: Yup.mixed().test(
      "confirmPassword",
      t("validationMessages.passwordsDoNotMatch"),
      function() {
        return this.parent.password === this.parent.confirmPassword;
      }
    )
  });
};

const UpdatePassword = ({ onChangePassword, version, logo }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  return (
    <Box className={classes.root}>
      <AppBar color="default" className={classes.headerBar}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          px={{ xs: 2, sm: 3 }}
        >
          <img height="42px" alt="Logo" src={logo}></img>
          <Typography
            variant="body2"
            color="textSecondary"
            data-qa="update-password-header-version"
          >
            {t("updatePassword.version", { version })}
          </Typography>
        </Box>
      </AppBar>
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
      >
        <Container maxWidth="md">
          <Paper>
            <Box
              px={{ xs: 4, sm: 9 }}
              pb={6}
              pt={8}
              display="flex"
              flexDirection="column"
              alignItems="center"
            >
              <img src={logo} alt="logo" height="74px" />
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                width="100%"
              >
                <Divider className={classes.divider} />
                <Typography variant="h3" color="textSecondary">
                  {t("updatePassword.changeYourPassword")}
                </Typography>
                <Formik
                  initialValues={{
                    password: "",
                    confirmPassword: ""
                  }}
                  validationSchema={geUpdatePasswordSchema(t)}
                  onSubmit={(values, { setSubmitting }) => {
                    setSubmitting(true);
                    onChangePassword(values);
                  }}
                >
                  {({ submitForm, isSubmitting }) => (
                    <Form className={classes.formContainer}>
                      <Field
                        variant="filled"
                        fullWidth
                        label={t("updatePassword.newPasswordFieldLabel")}
                        component={TextField}
                        type="password"
                        name="password"
                      />
                      <Field
                        variant="filled"
                        fullWidth
                        label={t("updatePassword.confirmPasswordFieldLabel")}
                        component={TextField}
                        name="confirmPassword"
                        type="password"
                      />
                      <Box mt={3}>
                        <Button
                          variant="contained"
                          onClick={submitForm}
                          color="primary"
                          type="submit"
                          disabled={isSubmitting}
                        >
                          {t("updatePassword.changePasswordBtn")}
                        </Button>
                      </Box>
                    </Form>
                  )}
                </Formik>
              </Box>
            </Box>
            <div className={classes.footer}>
              <CopyrightMessage />
            </div>
          </Paper>
        </Container>
      </Box>
    </Box>
  );
};

export default UpdatePassword;

UpdatePassword.propTypes = {
  onChangePassword: PropTypes.func,
  version: PropTypes.string,
  logo: PropTypes.any
};
